const TOUR_STORAGE_KEY = 'modelstack_tours'

interface TourState {
  [tourId: string]: {
    completed: boolean
    lastShown: string
    dismissCount: number
    startedCount: number
  }
}

export const tourStorage = {
  hasCompleted(tourId: string): boolean {
    const state = this.getState()
    return state[tourId]?.completed ?? false
  },

  markCompleted(tourId: string): void {
    const state = this.getState()
    state[tourId] = {
      ...state[tourId],
      completed: true,
      lastShown: new Date().toISOString(),
      dismissCount: state[tourId]?.dismissCount ?? 0,
      startedCount: state[tourId]?.startedCount ?? 1,
    }
    this.setState(state)
  },

  incrementDismissCount(tourId: string): void {
    const state = this.getState()
    const current = state[tourId] || {
      completed: false,
      lastShown: '',
      dismissCount: 0,
      startedCount: 0,
    }

    state[tourId] = {
      ...current,
      dismissCount: current.dismissCount + 1,
      lastShown: new Date().toISOString(),
    }

    this.setState(state)
  },

  incrementStartCount(tourId: string): void {
    const state = this.getState()
    const current = state[tourId] || {
      completed: false,
      lastShown: '',
      dismissCount: 0,
      startedCount: 0,
    }

    state[tourId] = {
      ...current,
      startedCount: current.startedCount + 1,
      lastShown: new Date().toISOString(),
    }

    this.setState(state)
  },

  shouldShow(tourId: string): boolean {
    const state = this.getState()
    const tour = state[tourId]

    if (!tour) return true
    if (tour.completed) return false
    if (tour.dismissCount >= 3) return false

    return true
  },

  shouldAutoStart(tourId: string): boolean {
    const state = this.getState()
    const tour = state[tourId]

    if (tour?.startedCount > 0) return false

    return this.shouldShow(tourId)
  },

  reset(tourId?: string): void {
    if (tourId) {
      const state = this.getState()
      delete state[tourId]
      this.setState(state)
    } else {
      localStorage.removeItem(TOUR_STORAGE_KEY)
    }
  },

  getState(): TourState {
    try {
      const stored = localStorage.getItem(TOUR_STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      console.error('Failed to parse tour state:', error)
      return {}
    }
  },

  setState(state: TourState): void {
    try {
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(state))
    } catch (error) {
      console.error('Failed to save tour state:', error)
    }
  },

  getAllTours(): TourState {
    return this.getState()
  },
}
